import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRole } from '@/hooks/use-role';
import { useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MapPin, Edit, Trash2, RotateCcw, Link2, Building2, Warehouse, Truck } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { BackToTopPill } from '@/components/BackToTopPill';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useLocations } from '../hooks/useLocations';
import { useLocationMutations } from '../hooks/useLocationMutations';
import LocationFormModal from './LocationFormModal';
import ClaimSharedLocationSheet from './ClaimSharedLocationSheet';
import SharedLocationsCard from './SharedLocationsCard';
import ConnectedVia3PLSection from './ConnectedVia3PLSection';
import ClientPendingClaimsCard from './ClientPendingClaimsCard';
import DelegateFulfilmentDialog from './DelegateFulfilmentDialog';
import {
  ThreePlModeDialog,
  ThreePlModeNeedsLocation,
  ThreePlModeOffer,
  TurnOffThreePlModeButton,
  THREE_PL_HEADING_SELECTOR,
  type ThreePlDirection,
} from './ThreePlMode';
import type { Location } from '../types';
import { isDelegatedLocation } from '../utils/delegatedLocations';

/**
 * BLU-389 Phase B/E — role-aware Locations page.
 *
 * Three sections, each rendered conditionally based on backend-derived role
 * surfaces (`getOrgRoleSurfaces`):
 *
 *   1. "Your locations"               — always visible (own physical sites).
 *   2. "Warehouses you fulfil for clients" — only when the org has
 *                                        is_3pl_provider=true AND owns
 *                                        locations (3PL surface).
 *   3. "3PL Connections"              — only when the org has at least one
 *                                        active or pending client junction
 *                                        (Shopify-client surface).
 *
 * Pure single-tenant orgs (the vast majority pre-BLU-367) see only section 1
 * with no microcopy referencing 3PL flows, plus (for an admin) the quiet
 * "Fulfil orders for other stores" offer in section 2's slot (BLU-1361), which
 * turns 3PL mode on. See ThreePlMode.tsx.
 */
export default function LocationManagement() {
  const { t } = useTranslation('inventory');
  const { t: tSettings } = useTranslation('settings');
  const { can } = useRole();
  // BLU-1361: 3PL mode is an org setting. The Settings admin tabs already
  // require this; checked again here so the switch never renders for anyone
  // the server (adminProcedure + requirePermission) would refuse.
  const canManageOrgSettings = can('org_settings.manage');
  const [threePlConfirm, setThreePlConfirm] = useState<ThreePlDirection | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [claimSheetOpen, setClaimSheetOpen] = useState(false);
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  const [delegateLocation, setDelegateLocation] = useState<Location | null>(null);
  // BLU-367 claim-shadow — set when the dialog is opened via the "Delegate
  // fulfilment" shortcut in ConnectedVia3PLSection so the target shared
  // location is pre-selected instead of the location's current delegation.
  const [delegatePresetTargetId, setDelegatePresetTargetId] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  // Build-time feature flag — gates ALL 3PL UI (claim button, role surfaces query).
  const sharedLocationsEnabled = (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';

  // BLU-879: this page reads the UNFILTERED list. It is where a delegation is
  // set, displayed via the "Fulfilled by" badge, re-targeted and cleared — and
  // `resolveDelegationLabel` resolves the delegation TARGET out of this same
  // list, so a filtered one would render a badge pointing at nothing.
  const { allLocations: locations, isLoading, error } = useLocations({
    includeInactive: true,
  });
  const { deleteLocation, reactivateLocation } = useLocationMutations();

  // BLU-389: backend-derived role surfaces. Single source of truth for which
  // sections to render. Falls back to "no 3PL surfaces" when the flag is off
  // (the query itself returns the safe default in that case).
  const roleSurfacesQuery = trpc.locations.getOrgRoleSurfaces.useQuery(undefined, {
    enabled: sharedLocationsEnabled,
    staleTime: 60_000,
  });
  const surfaces = roleSurfacesQuery.data ?? {
    is3plProvider: false,
    render3PLSurface: false,
    renderClientSurface: false,
    hasPendingClaims: false,
    pendingClaimCount: 0,
    ownedLocationCount: 0,
  };

  const handleEdit = (location: Location) => {
    setSelectedLocation(location);
    setFormModalOpen(true);
  };

  const handleDelete = (location: Location) => {
    setSelectedLocation(location);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedLocation) {
      deleteLocation.mutate({ locationId: selectedLocation.id }, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSelectedLocation(null);
        },
      });
    }
  };

  const handleReactivate = (location: Location) => {
    reactivateLocation.mutate({ locationId: location.id });
  };

  const handleDelegate = (location: Location, presetTargetId: string | null = null) => {
    setDelegateLocation(location);
    setDelegatePresetTargetId(presetTargetId);
    setDelegateDialogOpen(true);
  };

  const handleAddNew = () => {
    setSelectedLocation(null);
    setFormModalOpen(true);
  };

  const ownedLocations = locations.filter((loc) => !loc.is_shared);
  const sharedLocations = locations.filter((loc) => l_is_shared_active(loc));
  // Whether the org is ACTUALLY connected as a client (active/pending junction
  // or a shared-location row) — drives the role chip, not section visibility.
  const isClientConnected =
    sharedLocationsEnabled && (surfaces.renderClientSurface || sharedLocations.length > 0);
  // The 3PL Connections section renders whenever shared locations are enabled:
  // gating it on an EXISTING junction hid the "Claim 3PL location" button from
  // first-time orgs — a bootstrap dead-end (there was no way to enter the first
  // claim code from the UI). The section's empty state invites the first claim.
  const showClientSurface = sharedLocationsEnabled;
  const showProviderSurface = sharedLocationsEnabled && surfaces.render3PLSurface;
  // Loading state for the role-surfaces query — lets us avoid layout shift
  // when the query is in flight on first render.
  const surfacesLoading = sharedLocationsEnabled && roleSurfacesQuery.isLoading;
  // BLU-1361: section 2's slot when the 3PL section itself does not render.
  // Only on a CONFIRMED answer: a loading or failed read must never offer
  // 3PL mode to an org that is already a 3PL.
  const surfacesKnown = sharedLocationsEnabled && roleSurfacesQuery.isSuccess;
  const showThreePlOffer = surfacesKnown && canManageOrgSettings && !surfaces.is3plProvider;
  // 3PL mode is on but the org owns no location, so there is nothing to share yet.
  const showThreePlNeedsLocation =
    surfacesKnown && canManageOrgSettings && surfaces.is3plProvider && !surfaces.render3PLSurface;
  // After a change the button that opened the confirm is gone; land focus on
  // the heading that replaced it (the 3PL section, the offer or the hint).
  const focusThreePlSlot = (): boolean => {
    const heading = pageRef.current?.querySelector<HTMLElement>(THREE_PL_HEADING_SELECTOR);
    heading?.focus();
    return !!heading;
  };
  // Role label rendered as a small chip near the page title so admins can see
  // at a glance which surfaces apply to the active org. Dual-role orgs (3PL
  // provider that ALSO claims someone else's warehouse) get the combined label.
  const roleLabel = (() => {
    if (!sharedLocationsEnabled) return null;
    const isProvider = !!surfaces.is3plProvider && showProviderSurface;
    const isClient = !!isClientConnected;
    if (isProvider && isClient) return '3PL provider · Shopify-store client';
    if (isProvider) return '3PL provider';
    if (isClient) return 'Shopify-store client';
    return null;
  })();

  return (
    <div ref={pageRef} className="space-y-6 motion-safe:animate-fade-in">
      {/* ======================================================================
          Section 1 — Your locations (always visible)
          ====================================================================== */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Warehouse className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span>Your locations</span>
              {roleLabel && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="border-threadheads-purple/40 bg-threadheads-purple/5 text-threadheads-purple text-[10px] uppercase tracking-wide cursor-help font-normal"
                      >
                        Acting as · {roleLabel}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      {tSettings('locations.threePl.roleTooltip')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </CardTitle>
            <CardDescription>Warehouses and retail sites your team operates directly.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleAddNew}>
              <MapPin className="mr-2 h-4 w-4" aria-hidden="true" />
              Add location
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Timezone</TableHead>
                  <TableHead>Shopify ID</TableHead>
                  {/* BLU-495 Q4: `Auto-deactivate` column dropped — back-sync deprecated. */}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        Loading locations…
                      </div>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-destructive">
                      Error loading locations: {error.message}
                    </TableCell>
                  </TableRow>
                ) : ownedLocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <MapPin className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
                        <p className="text-muted-foreground">No locations yet</p>
                        <p className="text-sm text-muted-foreground">
                          Get started by adding your first warehouse or retail site.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  ownedLocations.map((location) => (
                    <TableRow key={location.id}>
                      <TableCell className="font-medium">
                        <div>{location.name}</div>
                        {can('inventory.view') && !isDelegatedLocation(location) && <Link className="text-xs text-threadheads-purple underline underline-offset-4" to={`/inventory/positions?location=${location.id}`}>
                          {t('positions.title')}
                        </Link>}
                        {sharedLocationsEnabled && location.delegated_to_location_id && (
                          <Badge
                            variant="outline"
                            className="mt-1 gap-1 border-threadheads-purple/30 bg-threadheads-purple/5 text-[10px] font-normal text-threadheads-purple"
                          >
                            <Link2 className="h-3 w-3" aria-hidden="true" />
                            Fulfilled by{' '}
                            {resolveDelegationLabel(location.delegated_to_location_id, locations)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{location.city}</TableCell>
                      <TableCell>{location.country_code}</TableCell>
                      <TableCell className="font-mono text-xs">{location.timezone}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {location.shopify_location_id}
                      </TableCell>
                      {/* BLU-495 Q4: `enable_auto_availability` badge cell dropped — back-sync deprecated. */}
                      <TableCell>
                        <Badge variant={location.is_active ? 'success' : 'secondary'}>
                          {location.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(location)}
                            disabled={!location.is_active}
                            aria-label={`Edit ${location.name}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>

                          {sharedLocationsEnabled && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelegate(location)}
                                    disabled={!location.is_active}
                                    aria-label={
                                      location.delegated_to_location_id
                                        ? `Manage fulfilment delegation for ${location.name}`
                                        : `Delegate fulfilment for ${location.name}`
                                    }
                                    className={
                                      location.delegated_to_location_id
                                        ? 'text-threadheads-purple hover:text-threadheads-purple'
                                        : undefined
                                    }
                                  >
                                    <Truck className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {location.delegated_to_location_id
                                    ? 'Manage fulfilment delegation'
                                    : 'Delegate fulfilment'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          {location.is_active ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(location)}
                              className="text-destructive hover:text-destructive"
                              aria-label={`Deactivate ${location.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReactivate(location)}
                              className="text-green-600 hover:text-green-700"
                              aria-label={`Reactivate ${location.name}`}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Loading skeleton for the role-surfaces query — keeps the layout
          stable on first paint so role-conditional sections don't pop in
          unexpectedly. Approximates one section's footprint (header + 1 card). */}
      {surfacesLoading && (
        <div className="space-y-4" aria-hidden="true">
          <div className="border-t border-muted-foreground/20 pt-6 space-y-2.5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-5 w-72" />
            </div>
            <Skeleton className="h-3.5 w-[28rem] max-w-full" />
          </div>
          <Skeleton className="h-44 w-full rounded-lg" />
        </div>
      )}

      {/* ======================================================================
          Section 2 — Warehouses you fulfil for clients (3PL provider surface)
          Renders only when the caller's org is flagged is_3pl_provider AND
          owns at least one location. The "Generate claim code" affordance
          lives inside SharedLocationsCard, which means a pure Shopify-client
          tenant never sees that button.
          ====================================================================== */}
      {!surfacesLoading && showProviderSurface && (
        <section className="space-y-4 motion-safe:animate-fade-in" aria-labelledby="provider-surface-heading">
          <div className="border-t border-muted-foreground/20 pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="h-4 w-4 text-threadheads-purple" aria-hidden="true" />
                  {/* h3 to match CardTitle heading level used in §1 (sibling rank).
                      tabIndex -1 + data-three-pl-heading: focus lands here after
                      3PL mode is turned on (BLU-1361). */}
                  <h3
                    id="provider-surface-heading"
                    tabIndex={-1}
                    data-three-pl-heading=""
                    className="text-base font-semibold tracking-tight outline-none"
                  >
                    Warehouses you fulfil for clients
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Generate a claim code for any of your warehouses to grant a Shopify-store client fulfilment access.
                </p>
              </div>
              {/* Org-wide, so once on the section, never on each location card. */}
              {canManageOrgSettings && (
                <TurnOffThreePlModeButton onClick={() => setThreePlConfirm('off')} />
              )}
            </div>
          </div>

          {ownedLocations
            .filter((loc) => loc.is_active)
            .map((loc) => (
              <SharedLocationsCard
                key={`shared-${loc.id}`}
                locationId={loc.id}
                locationName={loc.name}
              />
            ))}
        </section>
      )}

      {/* BLU-1361: section 2's slot when the 3PL section does not render.
          The offer (not a 3PL yet) or the add-a-location hint (3PL, no
          location). The confirm dialog below changes the mode. */}
      {!surfacesLoading && showThreePlOffer && (
        <ThreePlModeOffer onTurnOn={() => setThreePlConfirm('on')} />
      )}
      {!surfacesLoading && showThreePlNeedsLocation && (
        <ThreePlModeNeedsLocation onTurnOff={() => setThreePlConfirm('off')} />
      )}

      {/* ======================================================================
          Section 3 — 3PL Connections (Shopify-client surface)
          Two stacked cards: pending invitations (ClientPendingClaimsCard) and
          active connections (ConnectedVia3PLSection). The "Claim 3PL location"
          button moves into this section's header so it's only offered to orgs
          acting as a Shopify-store client.
          ====================================================================== */}
      {!surfacesLoading && showClientSurface && (
        <section className="space-y-4 motion-safe:animate-fade-in" aria-labelledby="client-surface-heading">
          <div className="border-t border-muted-foreground/20 pt-6">
            <div className="flex items-center justify-between gap-4 mb-1">
              <div>
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-threadheads-purple" aria-hidden="true" />
                  {/* h3 to match CardTitle heading level used in §1 (sibling rank). */}
                  <h3 id="client-surface-heading" className="text-base font-semibold tracking-tight">
                    3PL Connections
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Warehouses fulfilling your Shopify orders. Each connection is a separate 3PL partner.
                </p>
              </div>
              <Button variant="outline" onClick={() => setClaimSheetOpen(true)}>
                <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
                Claim 3PL location
              </Button>
            </div>
          </div>

          {/* Pending invitations sub-section (only when caller has pending claims) */}
          <ClientPendingClaimsCard />

          {/* Active connections — existing component, now inside the role-aware surface */}
          {sharedLocations.length > 0 ? (
            <ConnectedVia3PLSection
              locations={sharedLocations}
              ownLocations={ownedLocations}
              onDelegate={handleDelegate}
            />
          ) : (
            // Empty state for active connections renders unconditionally when
            // sharedLocations is empty. Copy is softened when pending claims
            // exist so the empty-card and pending-card together read as one
            // consistent narrative.
            <Card>
              <CardContent className="py-10 text-center">
                <Link2 className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <p className="text-sm font-medium">No active 3PL connections yet</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  {surfaces.hasPendingClaims
                    ? 'Your pending invitation above will move here once approved.'
                    : 'Once a 3PL provider approves your claim, their warehouse will appear here.'}
                </p>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      <LocationFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        location={selectedLocation}
      />

      {sharedLocationsEnabled && (
        <ClaimSharedLocationSheet
          open={claimSheetOpen}
          onOpenChange={setClaimSheetOpen}
          ownLocations={ownedLocations}
        />
      )}

      {sharedLocationsEnabled && canManageOrgSettings && (
        <ThreePlModeDialog
          direction={threePlConfirm}
          ownsNoLocation={surfaces.ownedLocationCount === 0}
          onClose={() => setThreePlConfirm(null)}
          focusAfterChange={focusThreePlSlot}
        />
      )}

      {sharedLocationsEnabled && (
        <DelegateFulfilmentDialog
          open={delegateDialogOpen}
          onOpenChange={setDelegateDialogOpen}
          location={delegateLocation}
          sharedLocations={sharedLocations}
          presetTargetId={delegatePresetTargetId}
        />
      )}

      {/* BLU-389 Phase E: brand-purple back-to-top pill for inventory-app
          consistency. The pill anchors to the SidebarInset scroll container,
          so it works correctly inside Settings tab content. */}
      <BackToTopPill />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the location &ldquo;{selectedLocation?.name}&rdquo;. Users will no longer
              be able to select this location, and it will not appear in reports. You can
              reactivate it later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deactivate location
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Internal helper kept colocated to make role-derivation logic explicit.
// `is_shared` is a derived UI flag set by the backend listLocations mapper
// when the location reached the caller via an active 3PL junction.
function l_is_shared_active(loc: Location): boolean {
  return loc.is_shared === true;
}

// Resolves a "Fulfilled by …" label for a delegated location's target. The
// target is a shared 3PL location, so its `owner_organization_name` names the
// 3PL provider. Falls back to "3PL partner" when the target is no longer in the
// list (e.g. the junction was later revoked) so the badge never reads blank.
function resolveDelegationLabel(targetId: string, all: Location[]): string {
  const target = all.find((loc) => loc.id === targetId);
  if (!target) return '3PL partner';
  const owner = target.owner_organization_name?.trim() || '3PL partner';
  return `${owner}: ${target.name}`;
}
